import { type MenuItemNode, buildMenuFromTabs, flattenLeaves } from '@/routes/buildMenuFromTabs'
import { getMenuIcon } from '@/routes/menuIcons'
import { useIsLogin } from '@/shared/context'
import { STORAGE, getAuthToken, setLocalStorage } from '@/shared/utils/storage'
import { Layout } from 'antd'
import type React from 'react'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import LayoutAdmin from './LayoutAdmin'

const { Content } = Layout
const layoutStyle: React.CSSProperties = {
  overflow: 'hidden',
  display: 'block',
}

interface MainLayoutProps {
  type?: string
  children?: React.ReactNode
}

const toAntdItem = (
  m: MenuItemNode
): {
  key: string
  label: React.ReactNode
  icon?: React.ReactNode
  children?: unknown[]
} => ({
  key: m.key,
  label: m.path ? (
    <Link to={m.path} style={{ color: 'inherit', textDecoration: 'none' }}>
      {m.label}
    </Link>
  ) : (
    m.label
  ),
  icon: getMenuIcon(m.icon),
  children: m.children?.map(toAntdItem),
})

// ponytail: type ('isAdmin'/'isUser') không còn phân nhánh menu — menu giờ lấy thẳng từ
// GetListTab (đồng bộ với quyền BE thực tế). Giữ prop để không phải sửa AdminRoutes/PrivateRoutes.
const MainLayout = ({ children }: MainLayoutProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const isLoginLocalStorage = getAuthToken()
  const { setIsLogin } = useIsLogin()

  const { listTabs } = useSelector((state: any) => state?.appGlobal)

  const menuItems = useMemo(() => buildMenuFromTabs(listTabs), [listTabs])
  const menu = useMemo(() => menuItems.map(toAntdItem), [menuItems])
  const pathByKey = useMemo(
    () => new Map(flattenLeaves(menuItems).map((m) => [m.key, m.path])),
    [menuItems]
  )
  const keyByPath = useMemo(
    () => new Map(flattenLeaves(menuItems).map((m) => [m.path, m.key])),
    [menuItems]
  )

  const currentKey = keyByPath.get(location.pathname)
  const selectedKey = [(location.state as any)?.activeMenu || currentKey || '']

  const onClickMenu = (menuInfo: any) => {
    setLocalStorage(STORAGE.KEY_MENU_ACTIVE, menuInfo.keyPath)
    const path = pathByKey.get(menuInfo.key)
    if (path) navigate(path)
  }

  useEffect(() => {
    if (isLoginLocalStorage) {
      setIsLogin(isLoginLocalStorage)
    }
  }, [isLoginLocalStorage])
  return (
    <Layout style={layoutStyle}>
      {menu?.length ? (
        <LayoutAdmin menuAdmin={menu} selectedKey={selectedKey} onClickMenu={onClickMenu}>
          {children}
        </LayoutAdmin>
      ) : (
        children
      )}
    </Layout>
  )
}

export default MainLayout
